import { useState, useEffect } from "react";
import { useComponentTranslation } from "@/services/i18n";
import toast from "react-hot-toast";
import api from "@/services/api";
import useStore from "@/services/store";
import Loader from "@/components/loader";
import { FiZap, FiSave } from "react-icons/fi";

const translations = {
  en: {
    title: "AI Screening",
    description: "Automatically score new applications against each job's screening criteria",
    enabled: "Auto-score new applications",
    enabled_help:
      "When enabled, every new application is queued for AI resume screening using the job's prompt (or the default). You can still run screening manually from the candidates table.",
    save: "Save",
    saving: "Saving...",
    success: "AI screening settings saved",
    error: "Error saving settings",
    fetch_error: "Error loading settings",
  },
  fr: {
    title: "Screening IA",
    description: "Score automatiquement les nouvelles candidatures selon les critères de chaque offre",
    enabled: "Score automatique des nouvelles candidatures",
    enabled_help:
      "Lorsque activé, chaque nouvelle candidature est envoyée au screening IA avec le prompt de l'offre (ou le prompt par défaut). Vous pouvez toujours lancer le screening manuellement depuis la liste des candidats.",
    save: "Enregistrer",
    saving: "Enregistrement...",
    success: "Paramètres de screening enregistrés",
    error: "Erreur lors de l'enregistrement",
    fetch_error: "Erreur lors du chargement",
  },
};

export default function Screening() {
  const { t } = useComponentTranslation(translations);
  const { organization, setOrganization } = useStore();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoScreeningEnabled, setAutoScreeningEnabled] = useState(false);

  useEffect(() => {
    fetchConfiguration();
  }, [organization?._id]);

  const fetchConfiguration = async () => {
    try {
      setLoading(true);
      const { ok, data } = await api.post("/organization/me", { organization_id: organization?._id });
      if (ok && data) {
        setAutoScreeningEnabled(data.auto_screening_enabled || false);
      }
    } catch (e) {
      console.error("Error fetching screening configuration:", e);
      toast.error(t("fetch_error"));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!organization?._id) return toast.error(t("error"));
    setSaving(true);

    try {
      const response = await api.put("/organization", {
        organization_id: organization._id,
        auto_screening_enabled: autoScreeningEnabled,
      });
      if (response.ok) {
        setOrganization(response.data);
        toast.success(t("success"));
      } else {
        toast.error(response.code || t("error"));
      }
    } catch (e) {
      console.error("Error saving screening configuration:", e);
      toast.error(e.code || e.message || t("error"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="max-w-3xl">
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-violet-100 rounded-lg flex items-center justify-center">
            <FiZap className="w-6 h-6 text-violet-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">{t("title")}</h2>
            <p className="text-sm text-gray-600 mt-1">{t("description")}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="auto_screening_enabled"
                name="auto_screening_enabled"
                type="checkbox"
                checked={autoScreeningEnabled}
                onChange={(e) => setAutoScreeningEnabled(e.target.checked)}
                className="w-4 h-4 text-primary bg-gray-100 border-gray-300 rounded focus:ring-primary"
              />
            </div>
            <div className="ml-3">
              <label htmlFor="auto_screening_enabled" className="input-label mb-0">
                {t("enabled")}
              </label>
              <p className="text-sm text-gray-500 mt-1">{t("enabled_help")}</p>
            </div>
          </div>

          <div className="flex justify-end border-t border-gray-200 pt-6">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? (
                <>
                  <Loader size="small" color="white" />
                  <span>{t("saving")}</span>
                </>
              ) : (
                <>
                  <FiSave className="w-4 h-4" />
                  <span>{t("save")}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
